import React, { useState } from 'react';
import MobileField from './MobileField';

/**
 * ⛔⛔ ADD OR CHANGE A PARTNER'S MOBILE — Card PARTNER-PHONE-EDIT, 3 Oct 2026.
 *
 * Card PARTNER-PHONE-SMS (#587) made a mobile mandatory at PROVISIONING, which
 * fixed every partner created from then on and nobody created before. Those
 * partners have no phone_number in the pool at all, cannot receive an SMS
 * fallback code, and until now there was no screen anywhere that could give
 * them one.
 *
 * ── ⛔⛔ IT NEVER PRE-FILLS A NUMBER, AND IT COULD NOT IF IT WANTED TO ───────
 *
 * The CRM holds `phone_last4` and nothing else (migration 181); the full E.164
 * lives only in Cognito, and the task role deliberately holds no AdminGetUser.
 * So there is no value to pre-fill and no call that could fetch one. The field
 * starts EMPTY and the staff member types the number the partner reads out.
 *
 * ⚠ That is a better interaction than it looks. A pre-filled number invites
 * "fix the last digit", which means staff edit a number they are not looking at
 * against a handset nobody has tested. Typing it whole means the partner says
 * it and the staff member reads it back — the confirmation step is the input.
 *
 * ⚠ The CURRENT value is shown, masked, beside the field, because "is there one
 * already?" is the question the staff member actually has. `•••• 0142` answers
 * it; an empty field alone does not distinguish "none on file" from "we just
 * don't show it here".
 *
 * ⛔ NO VERIFICATION IS CLAIMED. The server sends phone_number_verified=false
 * every time, and this panel says so: SMS is not wired yet (Twilio is deferred
 * until Daniel validates the conditions), so a partner cannot yet receive a
 * code at this number even once it is saved. Saying "2FA is now set up" would
 * be a promise the system cannot keep.
 */

/**
 * Mirrors database/partnerPhone.js — the SAME regex, not a looser UI one.
 *
 * ⛔ Card SALES-REP-EASE did NOT loosen this. <MobileField> converts the typed
 * national number to E.164 in the browser and hands up nothing else, so this
 * is now a shape assertion on a value the child already produced rather than a
 * check on free text. It stays because the shape is the contract with the
 * server, and the day somebody wires a different source into `value` is the
 * day it earns its place.
 */
const E164_RE = /^\+[1-9]\d{7,14}$/;

/**
 * ⚠ The runbook's wording, not the code's. Every code the server can return is
 * here; a missing one falls through to the raw code, which is exactly the "what
 * do I do now?" moment this mapping removes.
 */
export const ERROR_TEXT = Object.freeze({
  NO_PORTAL_IDENTITY:
    'This member has no portal sign-in yet, so there is no account to attach a mobile to. '
    + 'Use "Set up portal sign-in" on their row first — it takes the mobile in the same step.',
  COGNITO_USER_NOT_FOUND:
    'Partner not found in the sign-in system. The mobile was NOT changed. Escalate to Daniel.',
  COGNITO_UNAVAILABLE:
    'Sign-in system unavailable — AWS did not answer. The mobile was NOT changed. Wait a minute and retry.',
  POOL_NOT_CONFIGURED:
    'Sign-in system unavailable — the portal sign-in pool is not configured on this server yet. Escalate.',
  PHONE_REJECTED:
    'The sign-in system rejected that number. Check it with the partner and retype it in full international format.',
  PHONE_REQUIRED: 'Enter the mobile number.',
  PHONE_INVALID:
    'That number is not valid for the country selected. Check both with the partner '
    + 'and retype it — the field shows what will be saved before you press Save.',
  USER_NOT_FOUND: 'That partner member no longer exists. Reload the page.',
  DB_WRITE_FAILED:
    '⚠ Half-done: the sign-in system has the NEW mobile, but this screen still shows the old one. '
    + 'A code would go to the new handset. Run this again to make the display agree.',
});

export function errorText(code) {
  return ERROR_TEXT[code] || `The mobile was not changed (${code || 'unknown error'}). Escalate to Daniel.`;
}

/** `•••• 0142`, or an honest dash. Mirrors database/partnerPhone.maskedFromLast4. */
export function masked(last4) {
  const d = String(last4 == null ? '' : last4).trim();
  return /^\d{4}$/.test(d) ? `•••• ${d}` : '—';
}

export default function EditMobile({ member, canEdit, invoke, onSaved }) {
  const [open, setOpen] = useState(false);
  /** ⛔ E.164 or the empty string — never a half-typed national number. */
  const [value, setValue] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  /** Remount token for <MobileField>; see close(). */
  const [fieldKey, setFieldKey] = useState(0);

  // ⛔ THE SERVER IS THE GATE. This only decides whether to OFFER the control.
  if (!canEdit) return null;

  const trimmed = value.replace(/[\s ]/g, '');
  const valid = E164_RE.test(trimmed);

  function close() {
    setOpen(false); setValue(''); setErr(null);
  }

  async function save() {
    if (busy) return;
    /**
     * ⚠ REFUSED IN THE BROWSER FIRST. The server refuses it too — that is the
     * real gate — but a staff member sitting on the phone with the partner
     * should learn NOW, while they can simply ask for the number again.
     */
    if (!valid) { setErr(errorText('PHONE_INVALID')); return; }
    setBusy(true); setErr(null);
    try {
      const call = invoke || ((c, p) => window.zignagenixAPI.invoke(c, p));
      const r = await call('alliance_partner:members:set-mobile', {
        partnerUserId: member.id,
        mobile: trimmed,
      });
      if (!r || r.error) {
        setErr(errorText(r && r.code));
        return;
      }
      close();
      // ⚠ The parent is told the LAST FOUR, never the number it was given.
      if (onSaved) onSaved({ partnerUserId: member.id, phoneLast4: r.phoneLast4 });
    } catch (e) {
      setErr(errorText(null));
    } finally {
      setBusy(false);
    }
  }

  const btn = (bg, fg, bd) => ({
    padding: '5px 10px', fontSize: 11, fontWeight: 700, background: bg, color: fg,
    border: `1px solid ${bd}`, borderRadius: 5, cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.6 : 1,
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={member.phone_last4
          ? 'Change the mobile that receives sign-in codes.'
          : 'This member has no mobile on file and cannot receive a sign-in code by text.'}
        style={btn('#fff', '#2E8B98', '#BFE3E8')}>
        Edit mobile
      </button>
    );
  }

  return (
    <div style={{ flexBasis: '100%', marginTop: 6, padding: 10, background: '#F8F9FA', border: '1px solid #E9ECEF', borderRadius: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>
        Mobile for {member.email}
      </div>

      {/* ⚠ The current value, masked. The CRM has nothing else to show. */}
      <div style={{ fontSize: 11, color: '#495057', marginBottom: 6 }}>
        On file now: <strong>{masked(member.phone_last4)}</strong>
        {!member.phone_last4 && <span style={{ color: '#868E96' }}>{' '}— no mobile, so no sign-in code can be texted to them.</span>}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* ⛔⛔ Card SALES-REP-EASE — the country is ASKED FOR, not guessed.
            ⚠ STILL NEVER PRE-FILLS: the CRM holds four digits and the task role
            has no AdminGetUser, so there is no number to put here. The field is
            mounted empty and the picker starts at its default country. */}
        <MobileField
          key={fieldKey}
          idPrefix="edit-mobile"
          numberLabel="New mobile number"
          disabled={busy}
          onChange={(e164) => { setValue(e164); setErr(null); }}
        />
        {/* ⛔ DISABLED UNTIL THE BROWSER HAS A REAL E.164. <MobileField> hands
            up '' for anything it could not convert, so an invalid number cannot
            reach the handler even by a click. */}
        <button type="button" disabled={busy || !valid} onClick={save}
          style={{ ...btn('#2E8B98', '#fff', '#2E8B98'), opacity: busy || !valid ? 0.5 : 1 }}>
          Save mobile
        </button>
        <button type="button" disabled={busy} onClick={close} style={btn('#fff', '#495057', '#CED4DA')}>
          Cancel
        </button>
      </div>

      <div style={{ marginTop: 6, fontSize: 11, color: '#868E96' }}>
        {/* ⛔ Type it whole — see the header. There is deliberately nothing to edit. */}
        Pick their country, then type the number the way they read it out. The line
        above shows exactly what will be saved.
        {' '}
        {/* ⛔ NO VERIFICATION IS CLAIMED. */}
        <strong>Saving does not verify the handset</strong> — text messages are not
        switched on yet, so nothing is sent to this number today.
      </div>

      {err && (
        <div role="alert" style={{ marginTop: 6, fontSize: 11, color: '#991B1B', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 5, padding: '6px 8px' }}>
          {err}
        </div>
      )}
    </div>
  );
}
